/**
 * @file font-roles.ts
 * @description Реестр ролей шрифтов сайта и «какой шрифт у этой части
 * элемента» (phase19.16, приёмка Р2а, §7.17 п. 3; SB10, дополнение
 * 2026-10-11; проект uiux-fonts-usage §2–§4, §7).
 *
 * Один источник для трёх мест, которые иначе разойдутся:
 *   - вкладка «Оформление → Шрифты» кабинета: название роли, назначение,
 *     строка «Где:» и список «Все места» по группам (`FONT_ROLES`);
 *   - подсказка «Аа Шрифт заголовков · Montserrat» в панели элемента
 *     (`elementFontParts` + `effectiveFontKey`), а также в «Каталог →
 *     Карточка товара», «Каталог → Список» и подписи подвала;
 *   - контрактный тест витрины (элементы реестра рисуются ожидаемыми
 *     классами ролей).
 *
 * Списки описывают ЦЕЛЕВОЕ состояние витрины — после выравнивания ролей
 * (решение пользователя 2026-10-11, вопрос 1 проекта): заголовки кабинета
 * покупателя, входа, подтверждения заказа и окна отзыва — шрифтом заголовков;
 * название статьи и карточки ленты блога — шрифтом заголовков; имя
 * покупателя в меню — акцентом.
 *
 * Ролей в данных три (`theme.fonts`), четвёртая — «надписи капсом»
 * (`eyebrow`) — своего шрифта не имеет: берёт основной текст или шрифт
 * заголовков по `theme.type.eyebrowFrom` (css.ts, `--site-font-eyebrow`).
 * Акцент необязателен: без него — шрифт заголовков (css.ts, `accentKey`).
 * Стиль надписи в данных — `preset: "caps" | "script"` (до секции v4 ключ
 * назывался `font`; значения те же, подписи «Капсом» / «Акцентом»).
 *
 * SB120 этап 3а (db-design-sb120-stage3 §2): у части есть `key` — ключ
 * части `typo` элемента (реестр `TYPO_PART_KEYS`, element-typo.ts; тест
 * сверяет подмножество). Роли частей — «Авто» части: свой шрифт части
 * (`typo.<key>.font`) роль не меняет, подсказка разрешает его
 * `elementFontKey` с `partRole` отсюда.
 *
 * SB120 этап 3б (добавочно): ключи частей у категорий и рассылки (`main`),
 * статей (`category`, `title`, `excerpt`), отзывов (`title`, `text`) и
 * объявлений (`title`, `text`; у полосы и списка — только `text`). Роли
 * «Авто» прежние.
 *
 * Чистые данные и функции без зависимостей от рантайма; магазин-специфичных
 * значений нет (SB1).
 */

// Только типы из схемы секции: значение отсюда создало бы цикл schema → element-typo →
// element-font → font-roles → schema (константы ещё не созданы при первом импорте).
import type { SectionElement, SectionElementKind } from "../site-section/schema.js";
import type { SiteHeadingScale } from "./legacy-v1.js";
import type { SiteTheme } from "./schema.js";

// ── Роли ──

/** Роль шрифта — ключ `theme.fonts`: заголовки, основной текст, акцент. */
export type FontRole = keyof SiteTheme["fonts"];

/** Роли в порядке показа во вкладке «Шрифты». */
export const FONT_ROLE_KEYS = ["heading", "body", "accent"] as const satisfies readonly FontRole[];

/**
 * Роль части элемента: одна из трёх ролей или «надписи капсом», которые
 * своего шрифта не имеют и разрешаются `resolveFontRole`.
 */
export type FontPartRole = FontRole | "eyebrow";

/** Все допустимые роли частей — для проверок и тестов. */
export const FONT_PART_ROLES = [...FONT_ROLE_KEYS, "eyebrow"] as const satisfies readonly FontPartRole[];

/** Группы списка «Все места» в порядке показа. */
export const FONT_USAGE_GROUP_KEYS = ["pages", "catalog", "blog", "shell", "account"] as const;
export type FontUsageGroupKey = (typeof FONT_USAGE_GROUP_KEYS)[number];

/** Заголовки групп «Все места» (uiux-fonts-usage §3). */
export const FONT_USAGE_GROUP_TITLES: Readonly<Record<FontUsageGroupKey, string>> = {
  pages: "На страницах",
  catalog: "Товар и каталог",
  blog: "Блог",
  shell: "Шапка, корзина, заказ",
  account: "Кабинет покупателя",
};

export interface FontUsageGroup {
  group: FontUsageGroupKey;
  title: string;
  items: readonly string[];
}

export interface FontRoleInfo {
  /** Название роли во вкладке: «Заголовки». */
  title: string;
  /** Родительный падеж для подсказок: «Шрифт заголовков · Montserrat». */
  genitive: string;
  /** Одна строка назначения под названием роли. */
  purpose: string;
  /** Роль можно не выбирать (только акцент). */
  optional: boolean;
  /** Что стоит вместо невыбранной роли — подпись у необязательной. */
  fallbackNote?: string;
  /** Три самых заметных места — строка «Где:» (со строчной, через запятую). */
  brief: readonly string[];
  /** Список «Все места» по группам; пустых групп нет. */
  usage: readonly FontUsageGroup[];
  /** Частая ошибка ожидания — ⓘ под списком «Все места». */
  notThis?: string;
}

/** Группа «Все места» с заголовком из справочника — чтобы заголовки не расходились. */
function group(key: FontUsageGroupKey, items: readonly string[]): FontUsageGroup {
  return { group: key, title: FONT_USAGE_GROUP_TITLES[key], items };
}

/**
 * Реестр ролей шрифтов (uiux-fonts-usage §2 в целевом состоянии, §3;
 * SB10). Расширение — запись здесь, а не условия в кабинете.
 */
export const FONT_ROLES: Readonly<Record<FontRole, FontRoleInfo>> = {
  heading: {
    title: "Заголовки",
    genitive: "заголовков",
    purpose: "Крупные строки: заголовки блоков, название товара, цифры",
    optional: false,
    brief: ["заголовки блоков и секций", "название товара", "цифры"],
    usage: [
      group("pages", [
        "Заголовки блоков и секций",
        "Названия плиток, крупные цифры",
        "Заголовки отзывов, объявлений-карточек и статей в блоках",
      ]),
      group("catalog", [
        "Название товара",
        "Название категории в шапке каталога",
        "«Описание», «Продавец», «Отзывы» на странице товара",
        "«Категории», «Сортировка», «Фильтры» в каталоге",
      ]),
      group("blog", [
        "Заголовок ленты блога",
        "Название статьи и названия в карточках ленты",
        "Подзаголовки в тексте статьи",
      ]),
      group("shell", [
        "Название магазина в шапке, если нет логотипа",
        "«Ваша корзина», «Оформление заказа», «Заказ оформлен»",
        "Подтверждение заказа по ссылке из письма",
        "Правовые документы, страница 404",
      ]),
      group("account", [
        "Заголовки кабинета: «Мои заказы», «Профиль», «Чат»",
        "Окно входа и окно «Ваш отзыв»",
        "Подзаголовки карточек доставки",
      ]),
    ],
    notThis: "Не этим шрифтом: названия в карточках товаров — основным.",
  },
  body: {
    title: "Основной текст",
    genitive: "основного текста",
    purpose: "Всё, что читают: тексты, описания, карточки, меню, кнопки, цены",
    optional: false,
    brief: ["тексты блоков", "карточки товаров", "цены, кнопки и меню"],
    usage: [
      group("pages", [
        "Тексты блоков",
        "Кнопки и ссылки «Все →»",
        "Подписи плиток и цифр, таблицы",
        "Вопросы-ответы, контакты, подписка",
        "Тексты отзывов и объявлений, анонсы статей",
        "Название аудиозаписи",
      ]),
      group("catalog", [
        "Карточки товаров во всех шаблонах",
        "Плитки категорий и подкатегорий",
        "Цена, фасовки, количество, кнопка покупки, рейтинг",
        "Краткое описание, описание, сведения о товаре, характеристики, метки, бейджи",
        "Хлебные крошки, строки о продавце, похожие товары",
        "Текст в шапке категории, пункты сортировки и фильтров",
      ]),
      group("blog", [
        "Абзацы и списки статьи",
        "Рубрика и дата в карточках ленты и в статье",
        "Метки, оглавление, «Читайте также», автор, подпись к цитате",
      ]),
      group("shell", [
        "Меню шапки и меню на телефоне",
        "Колонки и контакты подвала",
        "Юридическая строка и копирайт",
        "Корзина и оформление заказа, кроме заголовков",
      ]),
      group("account", ["Тексты, формы и списки заказов в кабинете покупателя"]),
    ],
  },
  accent: {
    title: "Акцент",
    genitive: "акцента",
    purpose: "Надписи на фото и цитаты. Лучше всего — рукописный шрифт",
    optional: true,
    fallbackNote: "без него — шрифт заголовков",
    brief: ["надписи на фото", "цитаты в статьях", "подпись подвала"],
    usage: [
      group("pages", ["Элемент «Надпись» в стиле «Акцентом»", "Надписи на фото в стиле «Акцентом»"]),
      group("catalog", ["Категория под названием товара"]),
      group("blog", ["Цитаты в статьях"]),
      group("shell", ["Подпись в подвале"]),
      group("account", ["Имя покупателя в меню шапки"]),
    ],
  },
};

/** Число мест роли — для кнопки «Все места · N». */
export function fontRoleUsageCount(role: FontRole): number {
  return FONT_ROLES[role].usage.reduce((sum, g) => sum + g.items.length, 0);
}

// ── Надписи капсом (eyebrow) ──

/** Как надписи капсом называются в подсказках: «Надписи капсом — основного текста · Inter». */
export const EYEBROW_TITLE = "Надписи капсом";

/** Места надписей капсом — их шрифт задаёт `theme.type.eyebrowFrom` (uiux-fonts-usage §2). */
export const EYEBROW_USAGE: readonly string[] = [
  "Надпись над заголовком секции",
  "Элемент «Надпись» в стиле «Капсом»",
  "Надпись на фото в стиле «Капсом»",
  "Рубрика в элементе «Статьи блога»",
  "Надпись в шапке ленты блога",
];

/** Значение настройки `theme.type.eyebrowFrom`. */
export type EyebrowFrom = SiteTheme["type"]["eyebrowFrom"];

/** Подпись настройки во вкладке «Шрифты» и в истории изменений (diff.ts). */
export const EYEBROW_FROM_LABEL = "Надписи капсом над заголовками";

/** Варианты настройки «Надписи капсом над заголовками» в порядке показа. */
export const EYEBROW_FROM_OPTIONS: Readonly<Record<EyebrowFrom, string>> = {
  body: "Основным текстом",
  heading: "Шрифтом заголовков",
};

/** Строка под ролью, чьим шрифтом идут надписи капсом. */
export const EYEBROW_FROM_NOTE = "Надписи капсом над заголовками — тоже этим шрифтом";

// ── Размер заголовков сайта (SB10 «Размеры», 2026-10-11) ──

/** Подпись настройки `theme.type.headingScale` во вкладке «Шрифты» и в истории изменений. */
export const HEADING_SCALE_LABEL = "Размер заголовков";

/**
 * Подписи подсказок «Размера заголовков» в порядке показа (с SB120 этапа 2 размер —
 * число; ступени — подсказки-presets с множителями `HEADING_SCALE_V1`, legacy-v1.ts).
 */
export const HEADING_SCALE_OPTIONS: Readonly<Record<SiteHeadingScale, string>> = {
  s: "Мельче",
  m: "Обычный",
  l: "Крупнее",
};

// ── Разрешение роли в шрифт ──

/** Минимум темы, нужный для разрешения ролей (подходит и черновик, и опубликованная тема). */
export interface FontRoleTheme {
  readonly fonts: Readonly<Pick<SiteTheme["fonts"], FontRole>>;
  readonly type: Readonly<Pick<SiteTheme["type"], "eyebrowFrom">>;
}

/** Роль, чей шрифт выбран в данных для этой части: `eyebrow` → body/heading по `eyebrowFrom`. */
export function resolveFontRole(role: FontPartRole, theme: FontRoleTheme): FontRole {
  if (role !== "eyebrow") return role;
  return theme.type.eyebrowFrom === "heading" ? "heading" : "body";
}

export interface EffectiveFont {
  /** Ключ шрифта, который реально стоит на сайте (каталог или `shop:<id>`). */
  key: string;
  /** Роль, чей шрифт применён: у невыбранного акцента — `heading`. */
  role: FontRole;
  /** true — роль не выбрана, стоит запасной шрифт (акцент без шрифта → заголовков). */
  fallback: boolean;
}

/**
 * Какой шрифт стоит у части: роль → (eyebrow разрешается) → ключ. Акцент без
 * выбранного шрифта — шрифт заголовков, как в CSS-переменных (css.ts,
 * `accentKey`); подсказка тогда пишет «Шрифт акцента не выбран — сейчас
 * шрифт заголовков · …».
 */
export function effectiveFontKey(role: FontPartRole, theme: FontRoleTheme): EffectiveFont {
  const resolved = resolveFontRole(role, theme);
  if (resolved === "accent") {
    const accent = theme.fonts.accent;
    if (accent === null) return { key: theme.fonts.heading, role: "heading", fallback: true };
    return { key: accent, role: "accent", fallback: false };
  }
  return { key: theme.fonts[resolved], role: resolved, fallback: false };
}

// ── Части элементов ──

/**
 * Часть элемента и её роль; `part: ""` — у элемента одна роль, подпись части
 * не нужна. `key` — ключ части `typo` (есть у видов с `typo` и у шапки
 * раздела; нет — у части нет своих цвета и шрифта: карточки товаров,
 * название аудио, данные товара без `typo`).
 */
export interface FontPart {
  part: string;
  role: FontPartRole;
  key?: string;
}

/**
 * Что принимает `elementFontParts`: сохранённый элемент или черновик формы
 * кабинета (поля могут отсутствовать или быть неполными) — функция читает
 * поля осторожно и не падает.
 */
export type ElementFontInput = SectionElement | { readonly kind: string; readonly [key: string]: unknown };

const one = (role: FontPartRole, part = ""): FontPart[] => [{ part, role }];
/** Одна часть с ключом `main` (вид с одной частью `typo`). */
const main = (role: FontPartRole): FontPart[] => [{ part: "", role, key: "main" }];

/** Роль надписи по стилю `preset`: `script` («Акцентом») → accent, иначе («Капсом») → eyebrow. */
function captionRole(preset: unknown): FontPartRole {
  return preset === "script" ? "accent" : "eyebrow";
}

function field(el: ElementFontInput, key: string): unknown {
  return (el as Readonly<Record<string, unknown>>)[key];
}

function nonEmptyText(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/** Надписи поверх фото: одна роль — одна строка, оба стиля — две. */
function imageParts(el: ElementFontInput): FontPart[] {
  const overlays = field(el, "overlays");
  if (!Array.isArray(overlays) || overlays.length === 0) return [];
  const roles: FontPartRole[] = [];
  for (const overlay of overlays as unknown[]) {
    const preset = typeof overlay === "object" && overlay !== null ? (overlay as Record<string, unknown>).preset : undefined;
    const role = captionRole(preset);
    if (!roles.includes(role)) roles.push(role);
  }
  if (roles.length === 1) return one(roles[0] ?? "eyebrow", "Надпись на фото");
  return roles.map((role) => ({ part: role === "accent" ? "Надписи акцентом" : "Надписи капсом", role }));
}

function articlesParts(el: ElementFontInput): FontPart[] {
  const show = field(el, "show");
  // Рубрика выключена — её строку не показываем; нет поля (черновик) — показываем.
  const category =
    typeof show === "object" && show !== null ? (show as Record<string, unknown>).category !== false : true;
  return [
    ...(category ? [{ part: "Рубрика", role: "eyebrow" as const, key: "category" }] : []),
    { part: "Название", role: "heading", key: "title" },
    { part: "Анонс, дата и автор", role: "body", key: "excerpt" },
  ];
}

function announcementsParts(el: ElementFontInput): FontPart[] {
  // Заголовок есть только у карточек; полоса и список — одна строка текста (часть `text`;
  // `title` в данных хранится и при смене вида не теряется).
  if (field(el, "layout") !== "cards") return [{ part: "", role: "body", key: "text" }];
  return [
    { part: "Заголовок карточки", role: "heading", key: "title" },
    { part: "Текст", role: "body", key: "text" },
  ];
}

function productTitleParts(el: ElementFontInput): FontPart[] {
  return [
    { part: "Название", role: "heading", key: "title" },
    { part: "Категория под названием", role: "accent", key: "category" },
    ...(field(el, "showRating") === false ? [] : [{ part: "Рейтинг", role: "body" as const, key: "rating" }]),
  ];
}

/** «Продавец»: подпись-заголовок есть только в полном виде; «кратко» — одна строка текста. */
function productSellerParts(el: ElementFontInput): FontPart[] {
  if (field(el, "look") === "compact") return one("body");
  return [
    { part: "Подпись «Продавец»", role: "heading" },
    { part: "Сведения", role: "body" },
  ];
}

/**
 * Части и роли по виду элемента (uiux-fonts-usage §2, §4). Record по всем
 * видам схемы: новый вид элемента без записи здесь не соберётся.
 */
const ELEMENT_FONT_PARTS: Readonly<Record<SectionElementKind, (el: ElementFontInput) => FontPart[]>> = {
  heading: () => main("heading"),
  eyebrow: (el) => main(captionRole(field(el, "preset"))),
  text: () => main("body"),
  image: imageParts,
  gallery: () => [],
  // У видео нет подписи в данных — текста не бывает.
  video: () => [],
  audio: (el) => (nonEmptyText(field(el, "title")) ? one("body", "Название") : []),
  button: () => main("body"),
  tiles: () => [
    { part: "Название", role: "heading", key: "title" },
    { part: "Подпись и кнопка", role: "body", key: "caption" },
  ],
  products: () => one("body", "Карточки товаров"),
  // Этап 3б: у категорий одна часть `main` — подпись плитки; подпись части сохраняется.
  categories: () => [{ part: "Плитки категорий", role: "body", key: "main" }],
  articles: articlesParts,
  reviews: () => [
    { part: "Заголовок отзыва", role: "heading", key: "title" },
    { part: "Текст и имя", role: "body", key: "text" },
  ],
  faq: () => main("body"),
  contacts: () => main("body"),
  newsletter: () => main("body"),
  announcements: announcementsParts,
  stats: () => [
    { part: "Число", role: "heading", key: "number" },
    { part: "Подпись", role: "body", key: "label" },
  ],
  table: () => main("body"),
  breadcrumbs: () => main("body"),
  "product-gallery": () => [],
  "product-title": productTitleParts,
  "product-buy": () => one("body"),
  "product-badges": () => one("body"),
  "product-short": () => main("body"),
  "product-description": () => [
    { part: "Подпись «Описание»", role: "heading", key: "label" },
    { part: "Текст", role: "body", key: "text" },
  ],
  "product-facts": () => one("body"),
  "product-specs": () => one("body"),
  "product-tags": () => one("body"),
  "product-reviews": () => [
    { part: "Заголовок «Отзывы»", role: "heading" },
    { part: "Отзывы", role: "body" },
  ],
  "product-related": () => one("body", "Карточки товаров"),
  "product-seller": productSellerParts,
};

/**
 * Части элемента и их роли «Авто» — для подсказки «Шрифт: роль · имя» и для
 * `elementFontKey(…, { partRole })`. Пустой массив — текста у элемента не
 * бывает (фото без надписей, галерея, видео, аудио без названия, фото
 * товара): подсказку не показываем. Неизвестный вид — тоже [].
 *
 * С секции v4 «Шрифт» одним значением (`fontRole`) снят: шрифт выбирается
 * по частям (`typo.<key>.font`), роль здесь — то, что стоит при «Авто».
 * `key` — ключ части `typo` (подмножество `TYPO_PART_KEYS[kind]`).
 */
export function elementFontParts(element: ElementFontInput): FontPart[] {
  const kind = element.kind;
  if (!Object.prototype.hasOwnProperty.call(ELEMENT_FONT_PARTS, kind)) return [];
  return ELEMENT_FONT_PARTS[kind as SectionElementKind](element);
}

// ── Части мест вне секций ──

/** Шапка раздела секции (надпись, заголовок, ссылка справа). */
export const SECTION_HEADER_FONT_PARTS: readonly FontPart[] = [
  { part: "Надпись над заголовком", role: "eyebrow", key: "eyebrow" },
  { part: "Заголовок", role: "heading", key: "title" },
  { part: "Ссылка справа", role: "body", key: "link" },
];

/** «Каталог → Карточка товара»: все надписи карточки — основным текстом (все 4 шаблона). */
export const CATALOG_CARD_FONT_PARTS: readonly FontPart[] = [{ part: "Карточка товара (все надписи)", role: "body" }];

/** «Каталог → Список», шапка категории (после выравнивания — слушается «Начертания» и «Регистра»). */
export const CATEGORY_HEADER_FONT_PARTS: readonly FontPart[] = [
  { part: "Название категории", role: "heading" },
  { part: "Текст", role: "body" },
];

/** «Подвал → подпись». */
export const FOOTER_CAPTION_FONT_PARTS: readonly FontPart[] = [{ part: "", role: "accent" }];
